import { BookOpen, Circle, CircleCheck, Sparkles } from 'lucide-react'
import type { MealPlanDay } from '@hewey/shared'
import { dayLabel } from './dates.ts'
import styles from './DayRow.module.css'

type Props = {
	date: Date
	day: MealPlanDay | null
	isToday: boolean
	// A day that's already been and gone — faded, and never pressable.
	past: boolean
	// Draws the book icon, flagging the meal on the row as a linked recipe.
	// Not a link here: the row itself is the tap target.
	hasRecipe: boolean
	selected: boolean
	// Whether a tap does anything: past days never respond, and neither do
	// unselected days once a proposal is on screen.
	pressable: boolean
	// A meal being offered for this day, shown in place of the saved one
	// until it's saved or dropped.
	proposedTitle: string | null
	onPress: () => void
}

// Read-only day row with a check affordance — the shape both the planner's
// select mode and the recipe picker present days in. The note is always the
// saved one; a proposal only sets the meal.
export const DaySelectRow = ({
	date,
	day,
	isToday,
	past,
	hasRecipe,
	selected,
	pressable,
	proposedTitle,
	onPress,
}: Props) => {
	const { weekday, date: dateStr } = dayLabel(date)
	const title = proposedTitle ?? day?.title

	const content = (
		<>
			<span className={styles.check}>
				{proposedTitle !== null ? (
					<Sparkles size={18} aria-hidden />
				) : pressable ? (
					selected ? (
						<CircleCheck size={20} aria-hidden />
					) : (
						<Circle size={20} aria-hidden />
					)
				) : null}
			</span>
			<div className={styles.label}>
				<span className={styles.weekday}>{weekday}</span>
				<span className={styles.date}>{dateStr}</span>
			</div>
			<div className={styles.body}>
				<div className={styles.title}>
					{title || <span className={styles.placeholder}>—</span>}
				</div>
				{day?.note ? (
					<div className={styles.note}>{day.note}</div>
				) : null}
			</div>
			{hasRecipe ? (
				<span className={styles.gotoRecipe} aria-hidden>
					<BookOpen size={20} />
				</span>
			) : null}
		</>
	)

	const cls = [
		styles.row,
		styles.selectRow,
		isToday ? styles.today : '',
		past ? styles.readonly : '',
		hasRecipe ? styles.hasRecipe : '',
		proposedTitle !== null
			? styles.proposed
			: selected
				? styles.selected
				: '',
	]
		.filter(Boolean)
		.join(' ')

	return pressable ? (
		<button
			type="button"
			className={cls}
			// A proposed row swaps rather than deselects, so it isn't a
			// pressed/unpressed control.
			aria-pressed={proposedTitle !== null ? undefined : selected}
			onClick={onPress}
		>
			{content}
		</button>
	) : (
		<div className={cls}>{content}</div>
	)
}
